<script lang="ts">
import { sameOriginPath } from '@monolith/core/utilities/same-origin-path';
import { getFunnelEntry } from '@monolith/legacy/services/signup/registration-funnel-url';
import type { SuccessEvent } from '@monolith/legacy/types/auth-popin';

// Re-exported for the hosts that already import it from here; it lives in
// types/auth-popin.ts so AuthPopinProps.successCallback can be typed against it.
export type { SuccessEvent };

export function determineRedirectUrl(
  candidateBrandOnboardingLink: string | undefined,
  userRoles: string[] | undefined
): string {
  if (candidateBrandOnboardingLink) {
    return candidateBrandOnboardingLink;
  }
  const hasBrandAdminRole = !!userRoles?.includes('brand_admin');
  return hasBrandAdminRole ? '/v2/account/dashboard' : '/';
}

export function extractSuccessEventData(event?: SuccessEvent | null) {
  return {
    redirectUrl: event?.redirectUrl,
    onboardingLink: event?.data?.brand?.candidate?.onboarding_link,
    userRoles: event?.data?.user?.roles,
    hasBrandData: !!event?.data?.brand,
  };
}

/**
 * Pages where a login can complete but where reloading achieves nothing: the `guest`
 * middleware bounces the now-authenticated user straight back to the marketplace home.
 * The two registration funnel entries are auth pages in exactly the sense /login is —
 * a brand that logs in from /brand-registration has to reach its dashboard or its
 * onboarding link, not the marketplace.
 */
const AUTH_PAGE_PATHS = ['/', '/login', getFunnelEntry('retailer').path, getFunnelEntry('brand').path];

export function shouldRedirectToSpecificPage(pathname: string, hasBrandData: boolean): boolean {
  return AUTH_PAGE_PATHS.includes(pathname) && hasBrandData;
}

export const defaultSuccessCallback = (event?: SuccessEvent) => {
  const eventData = extractSuccessEventData(event);

  if (eventData.redirectUrl) {
    window.location.href = eventData.redirectUrl;
    return;
  }

  // `previous` is user-supplied, so it must never be able to send them off-site. A target
  // that cannot be reduced to a path falls through to the default below.
  const previousPath = sameOriginPath(new URLSearchParams(window.location.search).get('previous'));
  if (previousPath) {
    window.location.href = previousPath;
    return;
  }

  if (shouldRedirectToSpecificPage(window.location.pathname, eventData.hasBrandData)) {
    // determineRedirectUrl can return `onboarding_link`, which is pinned to
    // `config('app.url')`; following it from a language subdomain would cross origins and
    // leave the host-only session cookie behind.
    const redirect = determineRedirectUrl(eventData.onboardingLink, eventData.userRoles);
    window.location.href = sameOriginPath(redirect) ?? '/';
  } else {
    window.location.reload();
  }
};
</script>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Analytics from '@monolith/legacy/services/analytics';
import { FeatureFlag, isEnabled } from '@monolith/legacy/services/features';
import { AuthFormType } from '@monolith/legacy/types/auth-form-type';
import { BrandFormType } from '@monolith/legacy/types/brand-form-type';
import type { AuthPopinFormType } from '@monolith/legacy/types/auth-popin';
import { redirectToRegistrationFunnel } from '@monolith/legacy/services/signup/registration-funnel-url';
import type { SignupAudience } from '@monolith/legacy/types/signup-audience';
import { LiftPromocode } from '@monolith/legacy/types/order';
import { SignUpProps } from '@monolith/legacy/services/analytics/properties/sign-up-property';
import LoginForm from '@monolith/legacy/components/login-form.vue';
import LoginTwoFactor from '@monolith/legacy/components/two-factor-form.vue';
import LoginTwoFactorRecovery from '@monolith/legacy/components/two-factor-recovery-form.vue';
import ResetPasswordForm from '@monolith/legacy/components/reset-password-form.vue';
import ResetPasswordInputForm from '@monolith/legacy/components/reset-password-input-form.vue';
import { AsyncSignUp as SignUp } from '@monolith/legacy/components/sign-up/async-sign-up';
import { getSourceByPath } from '@monolith/legacy/services/analytics/events/home/source-property';
import { SignUpEventNames } from '@monolith/legacy/services/analytics/events/sign-up/common/event-names';
import SignUpEvent from '@monolith/legacy/services/analytics/events/sign-up/sign-up-event';
import useGlobalSelectors from '@core/composables/use-global-selectors/use-global-selectors';
import { getDeviceId } from '@monolith/legacy/services/fraud-tracker';
import EmailValidation from './email-validation.vue';
import { useEmailValidation } from '@monolith/legacy/composables/use-email-validation';
import { sendVerificationCode } from '@monolith/legacy/services/api/email-validation';
import { captureException } from '@core/plugins/sentry';

interface Props {
  liftCode?: LiftPromocode | null;
  brandName?: string;
  brandId?: string;
  view?: AuthPopinFormType;
  inPage?: boolean;
  isReminded?: boolean;
  successCallback?: (event?: SuccessEvent) => void;
  /**
   * A host that opens an audience-agnostic view (EmailOnlySignUp, EmailValidation) on behalf of one
   * funnel must state which, or the inference below silently picks retailer.
   */
  audience?: SignupAudience;
  /**
   * Seeds the email step instead of the `?email` param. Named `initialEmail` rather than `email`
   * because the live value is the store getter of that name below — it is a seed, not the current value.
   * Undefined means "not stated" and lets `?email` through; `''` is a deliberate empty seed and wins.
   */
  initialEmail?: string;
  /** Seeds an EmailValidation open with the token its code must be verified against. */
  verificationToken?: string;
}

const props = withDefaults(defineProps<Props>(), {
  liftCode: null,
  brandName: '',
  brandId: '',
  view: AuthFormType.Login,
  inPage: true,
  isReminded: false,
  successCallback: defaultSuccessCallback,
  audience: undefined,
  initialEmail: undefined,
  verificationToken: '',
});

const store = useStore();
const { t } = useI18n();

useGlobalSelectors();
const emailValidation = useEmailValidation();

// The popin opens brand mode only via an explicit BrandRegister view (brand CTA
// or brand entry page); everything else is the retailer funnel. Local state, not
// a shared marker: it is passed down to the shared email/code steps as a prop,
// so no other surface can observe or stale it.
const audience = ref<SignupAudience>(
  props.audience ?? (props.view === BrandFormType.BrandRegister ? 'brand' : 'retailer')
);

const form = ref<AuthPopinFormType>(props.view);
const liftBrandId = ref('');
const deviceId = ref('');
const verificationToken = ref(props.verificationToken);
const emailValidationRef = ref<InstanceType<typeof EmailValidation> | null>(null);

const isAnkorstart = computed(() => store.getters['signUp/isAnkorstart']);
const email = computed(() => store.getters['signUp/email']);
const liftBrandUrl = computed(() => store.getters['signUp/liftBrandUrl']);

const analyticProps = computed<SignUpProps>(() => ({
  audience: audience.value,
  is_personalized: isEnabled(FeatureFlag.SignUpPersonalize) && !!liftBrandId.value,
  is_reminded: props.isReminded,
  source: getSourceByPath(),
  is_opening_soon: isAnkorstart.value,
}));

const init = async () => {
  const params = new URLSearchParams(window.location.search);

  // BrandRegister view is purely an entry point into the funnel. The post-verify
  // landing is a real page (auth.brand-register-page), not a modal step, so brand
  // mode always enters the shared email step.
  if (props.view === BrandFormType.BrandRegister) {
    form.value = AuthFormType.EmailOnlySignUp;
  }

  store.commit('signUp/SET_FORM_VALUES', { email: props.initialEmail ?? params.get('email') ?? '' });
  const promocode = params.get('liftCode') || props.liftCode?.code || '';
  liftBrandId.value = params.get('liftBrandId') || props.brandId || '';
  if (promocode) {
    store.dispatch('signUp/setInitialPromocode', {
      promocode,
      brandName: props.brandName,
      liftAmount: props.liftCode?.amount,
    });
  }
  deviceId.value = await getDeviceId();
};

init();

function success(event: SuccessEvent = {}) {
  props.successCallback({
    redirectUrl: liftBrandUrl.value,
    ...event,
  });
}

// Hard-navigate into the current audience's funnel. Lift params are a
// retailer-funnel concern, so the brand handoff omits them.
function toRegistrationFunnel(formValues: { email?: string; verificationToken?: string } = {}) {
  redirectToRegistrationFunnel({
    audience: audience.value,
    email: formValues.email,
    verificationToken: formValues.verificationToken,
    lift: audience.value === 'retailer' ? { brandId: liftBrandId.value || undefined, code: props.liftCode?.code } : undefined,
  });
}

function switchForm(formType: AuthPopinFormType, formValues: { email?: string; verificationToken?: string } = {}) {
  if (formValues.verificationToken) {
    verificationToken.value = formValues.verificationToken;
  }
  form.value = formType;
  store.commit('signUp/SET_FORM_VALUES', formValues);
}

function onChangeToRegister() {
  form.value = AuthFormType.EmailOnlySignUp;
  Analytics.track(
    new SignUpEvent(SignUpEventNames.NoAccountYetEvent, {
      type: 'Click',
      ...analyticProps.value,
    })
  );
}

function onBackFromEmailValidation(emailValue: string) {
  form.value = AuthFormType.EmailOnlySignUp;
  store.commit('signUp/SET_FORM_VALUES', { email: emailValue });
}

async function handleVerify(code: string) {
  // Captured before the await, and re-used after it: a resend landing mid-verify rotates
  // `verificationToken` to a fresh, never-verified token (each send mints an independent one,
  // the previous stays valid). Re-reading the ref afterwards would hand the funnel that new
  // token, which fails the server-side gate and strands the token that was actually verified.
  const tokenUnderVerification = verificationToken.value;
  const verifySuccess = await emailValidation.verify(tokenUnderVerification, code, audience.value);
  if (verifySuccess) {
    // Diverge at verify: brand lands on /brand-registration, retailer on /register.
    toRegistrationFunnel({ email: email.value, verificationToken: tokenUnderVerification });
  } else {
    // Localized message; the FE can't distinguish invalid vs expired (both 422),
    // so one message covers both, consistent with the resend-failed handling.
    emailValidationRef.value?.setError(t('signup.step.email-validation.invalid-code'));
  }
}

function onChangeAudience(nextAudience: SignupAudience, nextEmail?: string) {
  audience.value = nextAudience;
  form.value = AuthFormType.EmailOnlySignUp;
  // Switching funnel re-renders the email step for the other audience; without this it
  // comes back empty even though the visitor just typed an address into it.
  if (nextEmail) {
    store.commit('signUp/SET_FORM_VALUES', { email: nextEmail });
  }
}

async function handleResend() {
  try {
    const result = await sendVerificationCode(email.value);
    const newToken = result?.data?.id;

    // A 2xx carrying no token means nothing was actually re-issued, so there is no success to
    // confirm: claiming one would wipe the digits the visitor is typing and leave them
    // verifying against the previous token. Fail instead -- the catch below shows the
    // retryable error and reports it, matching how the initial send treats a missing token.
    if (!newToken) {
      throw new Error('sendVerificationCode returned no verification token');
    }
    verificationToken.value = newToken;

    // Confirms the resend inline and clears the digit boxes: the token above just changed, so
    // anything already typed belongs to the previous code and would fail verification.
    emailValidationRef.value?.setResendSucceeded();
  } catch (err) {
    emailValidationRef.value?.setError(t('signup.step.email-validation.resend-failed'));
    captureException(`Failed to resend verification code: ${err}`);
  } finally {
    emailValidationRef.value?.setResending(false);
  }
}
</script>

<template>
  <div
    :class="{
      'popin-container': true,
      'popin-in-page': inPage,
    }"
    data-testid="login-popin-container"
  >
    <div class="popin">
      <div class="popin__content">
        <div class="popin__form-col modal-body">
          <LoginForm
            v-if="form === AuthFormType.Login"
            :prefilled-email="email"
            :device-id="deviceId"
            :audience="audience"
            @success="success"
            @change-to-register="onChangeToRegister"
            @change-to-two-factor="form = AuthFormType.TwoFactor"
            @change-to-reset-password="form = AuthFormType.ResetPassword"
          />
          <ResetPasswordForm
            v-else-if="form === AuthFormType.ResetPassword"
            @change-to-login="form = AuthFormType.Login"
          />
          <LoginTwoFactor
            v-else-if="form === AuthFormType.TwoFactor"
            @change-to-two-factor-recovery="form = AuthFormType.TwoFactorRecovery"
          />
          <LoginTwoFactorRecovery
            v-else-if="form === AuthFormType.TwoFactorRecovery"
            @change-to-two-factor="form = AuthFormType.TwoFactor"
          />
          <ResetPasswordInputForm
            v-else-if="form === AuthFormType.ResetPasswordInput"
            @change-to-login="form = AuthFormType.Login"
          />
          <SignUp
            v-else-if="form === AuthFormType.EmailOnlySignUp"
            :key="audience"
            :device-id="deviceId"
            :form-type="form"
            :analytic-props="analyticProps"
            :referral-brand-id="liftBrandId"
            @success="success"
            @redirect="switchForm"
            @change-audience="onChangeAudience"
          />
          <EmailValidation
            v-else-if="form === AuthFormType.EmailValidation"
            ref="emailValidationRef"
            :email="email"
            :audience="audience"
            @verify="handleVerify"
            @resend="handleResend"
            @back="onBackFromEmailValidation"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@css/vue-import';

.popin {
  &__content {
    @apply ds-flex;
  }

  &__img-col,
  &__form-col {
    @apply ds-flex-1;
  }

  &__form-col {
    @apply ds-text-left;
  }

  &__img-col {
    @apply ds-hidden md:ds-flex ds-text-primary ds-bg-no-repeat ds-bg-cover ds-flex-col;
    background: url(/images/modal/background-signup-modal.png) 0 0;
    min-height: 568px;
  }
}

.popin-container {
  @apply ds-bg-white ds-relative;

  .popin-list__bullet {
    @apply ds-leading-5;
  }

  .popin-content {
    @apply ds-overflow-y-auto;
  }
}

.popin-in-page {
  @apply ds-px-[10px] ds-max-w-[100%] md:ds-p-[64px] md:ds-w-[528px] md:ds-max-w-[528px] md:ds-m-auto;
}
</style>
